import { Head, Link, usePage } from "@inertiajs/react"
import { ChevronLeft } from "lucide-react"
import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { CreatorMasterclassCompletedBlock } from "@/components/learn/creator-masterclass-completed-block"
import { CreatorMasterclassMarkCompleteForm } from "@/components/learn/creator-masterclass-mark-complete-form"
import { CreatorMasterclassModulesSection } from "@/components/learn/creator-masterclass-modules-section"
import { MasterclassCover } from "@/components/learn/masterclass-cover"
import { Badge } from "@/components/ui/badge"
import AppLayout from "@/layouts/app-layout"
import { learnMasterclassesPath } from "@/routes"
import type { CreatorLearnShowProps } from "@/types/learn"

/**
 * OKS-56 / OKS-602 — Creator-side `/learn/:id` detail page.
 *
 * OKS-602 adds the Modules section + derived completion:
 *   - `has_modules` true  → render the Modules section (per-module state
 *     + navigation). Completion is DERIVED (written server-side when the
 *     last module completes); the legacy whole-masterclass mark-complete
 *     form is NOT shown. The completed block renders once `completion` is
 *     present.
 *   - `has_modules` false → zero-module fallback: keep the OKS-56 legacy
 *     mark-complete form / completed block (PRD Q4).
 *
 * # Toast discrimination (TRD KD-8)
 *
 * Redirects land here after both the module-completion and the legacy
 * masterclass-completion POSTs. One toast per genuine completion:
 *   - `flash.masterclass_completed` (derived, from the last module) →
 *     masterclass-complete toast + focus shift to the completed block.
 *     Takes priority so the final module shows ONE (bigger) toast.
 *   - else `flash.just_completed` → a module was completed
 *     (`has_modules`) OR the legacy masterclass button fired
 *     (zero-module fallback).
 * Idempotent re-submits carry no flash → no toast (UX Spec §4).
 */
interface PageFlash {
  just_completed?: boolean
  masterclass_completed?: boolean
}

export default function CreatorLearnShow({
  masterclass,
  completion,
  modules,
  has_modules,
  completed_count,
}: CreatorLearnShowProps) {
  const { t, i18n } = useTranslation()
  const completedRef = useRef<HTMLDivElement | null>(null)

  const { flash } = usePage<{ flash?: PageFlash }>().props
  const justCompleted = flash?.just_completed === true
  const masterclassCompleted = flash?.masterclass_completed === true

  // One toast per genuine completion. Masterclass-complete wins over the
  // per-module toast so the final module shows a single (bigger) toast.
  useEffect(() => {
    if (masterclassCompleted) {
      toast.success(t("learn.flash.masterclass_completed"))
    } else if (justCompleted) {
      toast.success(
        has_modules
          ? t("learn.flash.module_marked_complete")
          : t("learn.flash.marked_complete"),
      )
    }
  }, [masterclassCompleted, justCompleted, has_modules, t])

  // A11y focus-shift onto the completed block when the masterclass just
  // became complete (mirrors the OKS-56 focus discipline).
  useEffect(() => {
    if (masterclassCompleted && completedRef.current) {
      completedRef.current.focus()
    }
  }, [masterclassCompleted])

  const publishedDate = new Date(masterclass.published_at)
  const publishedFormatted = publishedDate.toLocaleDateString(
    i18n.language === "en" ? "en-US" : "fr-FR",
    { day: "numeric", month: "short", year: "numeric" },
  )

  const isCompleted = completion !== null

  return (
    <AppLayout>
      <Head title={masterclass.title} />
      <main className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-6 pb-32 lg:pb-6">
        <Link
          href={learnMasterclassesPath()}
          className="text-muted-foreground inline-flex items-center gap-1 self-start text-sm hover:underline focus:outline-none"
          data-testid="learn-show-back"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          {t("learn.show.back_to_index")}
        </Link>

        <header className="flex flex-col gap-3">
          <MasterclassCover
            id={masterclass.id}
            coverImageUrl={masterclass.cover_image_url}
            title={masterclass.title}
            size="card"
            className="rounded-md"
            data-testid={`learn-show-${masterclass.id}-cover`}
          />
          <h1
            className="text-2xl font-bold tracking-tight"
            data-testid="learn-show-title"
          >
            {masterclass.title}
          </h1>
          <div className="flex items-center gap-2">
            <Badge variant="outline" data-testid="learn-show-topic">
              {masterclass.topic}
            </Badge>
            <span className="text-muted-foreground text-xs">
              {t("learn.show.published_at_relative", {
                relative: publishedFormatted,
              })}
            </span>
          </div>
        </header>

        <section
          className="text-sm leading-relaxed whitespace-pre-wrap"
          data-testid="learn-show-description"
        >
          {masterclass.description}
        </section>

        {/* OKS-602 — Modules section (when the masterclass has modules) */}
        {has_modules && (
          <CreatorMasterclassModulesSection
            masterclassId={masterclass.id}
            modules={modules}
            completedCount={completed_count}
          />
        )}

        {/*
          Completed block renders whenever completion is present (derived
          from all-modules-complete, or the legacy button on zero-module).
          The legacy mark-complete FORM shows ONLY for a zero-module
          masterclass that is not yet complete (PRD Q4 fallback).
        */}
        {isCompleted ? (
          /* OKS-638 — offset above the tab bar, which now reserves the iOS bottom
             safe-area inset under its 4rem row — keeps this CTA stacked above the
             grown bar; on non-inset devices calc() resolves to 4rem (old bottom-16). */
          <div
            className="bg-background/95 supports-[backdrop-filter]:bg-background/85 fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-30 border-t p-3 backdrop-blur lg:static lg:inset-auto lg:bottom-auto lg:border-0 lg:bg-transparent lg:p-0"
            data-testid="learn-show-action-region"
          >
            <CreatorMasterclassCompletedBlock
              ref={completedRef}
              completedAt={completion.completed_at}
            />
          </div>
        ) : (
          !has_modules && (
            /* OKS-638 — zero-module fallback CTA: same bottom safe-area offset
               as the completed branch above so the mark-complete form clears the
               grown tab bar on iOS (calc()=4rem off-inset == old bottom-16). */
            <div
              className="bg-background/95 supports-[backdrop-filter]:bg-background/85 fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-30 border-t p-3 backdrop-blur lg:static lg:inset-auto lg:bottom-auto lg:border-0 lg:bg-transparent lg:p-0"
              data-testid="learn-show-action-region"
            >
              <CreatorMasterclassMarkCompleteForm
                masterclassId={masterclass.id}
                className="flex justify-center sm:justify-start"
              />
            </div>
          )
        )}
      </main>
    </AppLayout>
  )
}
